import React, { useState, useRef } from 'react'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
// import { Header } from './components/Header'
import { Sidebar } from './components/Sidebar'
import { ComicCanvas } from './components/ComicCanvas'
import { FloatingPanel } from './components/FloatingPanel'
import { ExportPanel } from './components/ExportPanel'
import { ComicTemplate, ComicPanel, DialogBubbleData, COMIC_TEMPLATES, calculatePanelLayout, ImageAsset } from './types'

function App() {
  const [currentTemplate, setCurrentTemplate] = useState<ComicTemplate>(COMIC_TEMPLATES[0])
  const [panels, setPanels] = useState<ComicPanel[]>(COMIC_TEMPLATES[0].panels)
  const [dialogBubbles, setDialogBubbles] = useState<DialogBubbleData[]>([])
  const [selectedBubble, setSelectedBubble] = useState<string | null>(null)
  const [selectedPanel, setSelectedPanel] = useState<string | null>(COMIC_TEMPLATES[0].panels[0]?.id || null)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [showFloatingPanel, setShowFloatingPanel] = useState(false)
  const [showExportPanel, setShowExportPanel] = useState(false)
  const [title, setTitle] = useState('')
  const [watermark, setWatermark] = useState('')
  const canvasRef = useRef<HTMLDivElement>(null)
  const [imageAssets, setImageAssets] = useState<ImageAsset[]>([])
  const [canvasBgColor, setCanvasBgColor] = useState('#ffffff')

  React.useEffect(() => {
    const handler = (e: any) => {
      setCanvasBgColor(e.detail)
    }
    window.addEventListener('canvas-bg-color', handler)
    return () => window.removeEventListener('canvas-bg-color', handler)
  }, [])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over, delta } = event
    
    // 处理气泡拖拽
    const draggedBubbleId = active.id as string
    const draggedBubble = dialogBubbles.find(b => b.id === draggedBubbleId)
    
    if (draggedBubble && delta) {
      // 计算新位置
      const newX = draggedBubble.x + delta.x
      const newY = draggedBubble.y + delta.y
      
      // 如果拖拽到了不同的面板
      if (over && active.id !== over.id) {
        const overData = over.data?.current
        
        if (overData?.type === 'panel') {
          const targetPanelId = overData.panelId
          
          if (draggedBubble.panelId !== targetPanelId) {
            // 找到目标面板
            const targetPanel = panels.find(p => p.id === targetPanelId)
            if (targetPanel) {
              // 更新气泡的面板ID，并调整位置到目标面板内
              updateDialogBubble(draggedBubbleId, {
                panelId: targetPanelId,
                x: Math.max(10, Math.min(newX, targetPanel.width - draggedBubble.width - 10)),
                y: Math.max(10, Math.min(newY, targetPanel.height - draggedBubble.height - 10))
              })
            }
          } else {
            // 在同一面板内移动
            updateDialogBubble(draggedBubbleId, { x: newX, y: newY })
          }
        }
      } else {
        // 普通拖拽移动
        updateDialogBubble(draggedBubbleId, { x: newX, y: newY })
      }
    }
    
    // 延迟清除拖拽状态，让动画更平滑
    setTimeout(() => {
      setActiveDragId(null)
    }, 100)
  }

  const handleTemplateChange = (template: ComicTemplate) => {
    // 动态计算新模板的画布尺寸
    const layoutType = template.type === 'single' ? 'vertical' : 'grid'
    const panelCount = panels.length || template.panels.length
    
    let canvasWidth = template.width
    let canvasHeight = template.height
    
    if (layoutType === 'vertical') {
      // 垂直布局：高度需要动态调整
      const margin = 20
      const panelSpacing = 15
      const panelWidth = canvasWidth - (margin * 2)
      const panelHeight = Math.max(panelWidth / (4/3), 150) // 4:3比例，最小150px
      canvasHeight = Math.max(
        panelCount * panelHeight + (panelCount - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    } else if (layoutType === 'grid') {
      // 网格布局：可能需要调整高度
      const cols = 2
      const rows = Math.ceil(panelCount / cols)
      const margin = 20
      const panelSpacing = 15
      const availableWidth = canvasWidth - (margin * 2) - ((cols - 1) * panelSpacing)
      const panelWidth = Math.max(availableWidth / cols, 180)
      const panelHeight = Math.max(panelWidth / 1.5, 135) // 保持合理宽高比
      
      canvasHeight = Math.max(
        rows * panelHeight + (rows - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    }
    
    // 使用新的画布尺寸重新计算面板布局
    const newPanels = calculatePanelLayout(panelCount, canvasWidth, canvasHeight, layoutType)
    
    // 创建更新后的模板
    const updatedTemplate = {
      ...template,
      panels: newPanels,
      width: canvasWidth,
      height: canvasHeight
    }
    
    setCurrentTemplate(updatedTemplate)
    setPanels(newPanels)
    setDialogBubbles([]) // 清空对话气泡
    setSelectedBubble(null)
    setSelectedPanel(newPanels[0]?.id || null) // 默认选择第一个面板
  }

  const addDialogBubble = (type: 'speech' | 'thought' | 'shout' | 'explode', panelId?: string) => {
    const targetPanelId = panelId || selectedPanel || panels[0]?.id
    if (!targetPanelId) return

    const newBubble: DialogBubbleData = {
      id: Date.now().toString(),
      type,
      text: type === 'speech' ? '在这里输入对话...' : 
            type === 'thought' ? '在这里输入想法...' : 
            type === 'shout' ? '在这里输入喊叫内容...' :
            '在这里输入爆炸内容...',
      x: 20,
      y: 20,
      width: 120,
      height: 60,
      fontSize: type === 'shout' ? 16 : 14,
      fontFamily: 'Inter',
      color: type === 'thought' ? '#1e40af' : 
             type === 'shout' ? '#dc2626' : 
             type === 'explode' ? '#f59e0b' :
             '#000000',
      backgroundColor: type === 'thought' ? '#dbeafe' : 
                      type === 'shout' ? '#fef2f2' : 
                      type === 'explode' ? 'transparent' :
                      '#ffffff',
      borderColor: type === 'thought' ? '#3b82f6' : 
                   type === 'shout' ? '#dc2626' : 
                   type === 'explode' ? '#f59e0b' :
                   '#000000',
      borderWidth: type === 'shout' ? 4 : 2,
      borderRadius: type === 'thought' ? 50 : 
                    type === 'shout' ? 4 : 
                    type === 'explode' ? 0 :
                    16,
      padding: 8,
      rotation: 0,
      sequence: null,
      panelId: targetPanelId,
      arrowDirection: 'down'
    }
    
    setDialogBubbles(prev => [...prev, newBubble])
    setSelectedBubble(newBubble.id)
    setShowFloatingPanel(true)
  }

  const updateDialogBubble = (id: string, updates: Partial<DialogBubbleData>) => {
    setDialogBubbles(prev => 
      prev.map(bubble => 
        bubble.id === id ? { ...bubble, ...updates } : bubble
      )
    )
  }

  const deleteDialogBubble = (id: string) => {
    setDialogBubbles(prev => prev.filter(bubble => bubble.id !== id))
    if (selectedBubble === id) {
      setSelectedBubble(null)
      setShowFloatingPanel(false)
    }
  }

  const updatePanel = (id: string, updates: Partial<ComicPanel>) => {
    setPanels(prev => 
      prev.map(panel => 
        panel.id === id ? { ...panel, ...updates } : panel
      )
    )
  }

  // 添加面板 - 智能布局，避免重叠
  const addPanel = () => {
    if (panels.length >= 12) {
      alert('最多支持12个面板')
      return
    }

    // 根据当前模板类型重新计算所有面板布局
    const layoutType = currentTemplate.type === 'single' ? 'vertical' : 'grid'
    
    // 动态计算画布尺寸
    const newPanelCount = panels.length + 1
    let canvasWidth = currentTemplate.width
    let canvasHeight = currentTemplate.height
    
    if (layoutType === 'vertical') {
      // 垂直布局：高度需要动态增加
      const margin = 20
      const panelSpacing = 15
      const panelWidth = canvasWidth - (margin * 2)
      const panelHeight = Math.max(panelWidth / (4/3), 150) // 4:3比例，最小150px
      canvasHeight = Math.max(
        newPanelCount * panelHeight + (newPanelCount - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    } else if (layoutType === 'grid') {
      // 网格布局：可能需要增加高度
      const cols = 2
      const rows = Math.ceil(newPanelCount / cols)
      const margin = 20
      const panelSpacing = 15
      const availableWidth = canvasWidth - (margin * 2) - ((cols - 1) * panelSpacing)
      const panelWidth = Math.max(availableWidth / cols, 150)
      const panelHeight = Math.max(panelWidth / 1.5, 120) // 保持合理宽高比
      
      canvasHeight = Math.max(
        rows * panelHeight + (rows - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    }
    
    const newPanels = calculatePanelLayout(
      newPanelCount, 
      canvasWidth, 
      canvasHeight, 
      layoutType
    )
    
    // 保留现有面板的背景图片和自定义属性
    const updatedPanels = newPanels.map((newPanel, index) => {
      const existingPanel = panels[index]
      if (existingPanel) {
        return {
          ...newPanel,
          backgroundImage: existingPanel.backgroundImage,
          backgroundColor: existingPanel.backgroundColor,
          borderColor: existingPanel.borderColor,
          borderWidth: existingPanel.borderWidth,
          borderRadius: existingPanel.borderRadius
        }
      }
      return newPanel
    })

    setPanels(updatedPanels)
    
    // 选择新添加的面板
    setSelectedPanel(updatedPanels[updatedPanels.length - 1].id)
    
    // 更新模板信息，包括新的画布尺寸
    setCurrentTemplate(prev => ({
      ...prev,
      panels: updatedPanels,
      width: canvasWidth,
      height: canvasHeight
    }))
  }

  // 删除面板
  const removePanel = () => {
    if (panels.length <= 1) {
      alert('至少需要保留1个面板')
      return
    }

    // 根据当前模板类型重新计算面板布局
    const layoutType = currentTemplate.type === 'single' ? 'vertical' : 'grid'
    
    // 动态计算画布尺寸
    const newPanelCount = panels.length - 1
    let canvasWidth = currentTemplate.width
    let canvasHeight = currentTemplate.height
    
    if (layoutType === 'vertical') {
      // 垂直布局：高度需要动态调整
      const margin = 20
      const panelSpacing = 15
      const panelWidth = canvasWidth - (margin * 2)
      const panelHeight = Math.max(panelWidth / (4/3), 150) // 4:3比例，最小150px
      canvasHeight = Math.max(
        newPanelCount * panelHeight + (newPanelCount - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    } else if (layoutType === 'grid') {
      // 网格布局：可能需要减少高度
      const cols = 2
      const rows = Math.ceil(newPanelCount / cols)
      const margin = 20
      const panelSpacing = 15
      const availableWidth = canvasWidth - (margin * 2) - ((cols - 1) * panelSpacing)
      const panelWidth = Math.max(availableWidth / cols, 150)
      const panelHeight = Math.max(panelWidth / 1.5, 120) // 保持合理宽高比
      
      canvasHeight = Math.max(
        rows * panelHeight + (rows - 1) * panelSpacing + margin * 2,
        400 // 最小高度
      )
    }
    
    const newPanels = calculatePanelLayout(
      newPanelCount, 
      canvasWidth, 
      canvasHeight, 
      layoutType
    )
    
    // 保留现有面板的背景图片和自定义属性
    const updatedPanels = newPanels.map((newPanel, index) => {
      const existingPanel = panels[index]
      if (existingPanel) {
        return {
          ...newPanel,
          backgroundImage: existingPanel.backgroundImage,
          backgroundColor: existingPanel.backgroundColor,
          borderColor: existingPanel.borderColor,
          borderWidth: existingPanel.borderWidth,
          borderRadius: existingPanel.borderRadius
        }
      }
      return newPanel
    })

    setPanels(updatedPanels)
    
    // 清除与被删除面板相关的对话气泡
    const removedPanelId = panels[panels.length - 1].id
    setDialogBubbles(prev => prev.filter(bubble => bubble.panelId !== removedPanelId))
    
    // 如果当前选中的是被删除的面板，则选择最后一个面板
    if (selectedPanel === removedPanelId) {
      setSelectedPanel(updatedPanels[updatedPanels.length - 1]?.id || null)
    }
    
    // 更新模板信息，包括新的画布尺寸
    setCurrentTemplate(prev => ({
      ...prev,
      panels: updatedPanels,
      width: canvasWidth,
      height: canvasHeight
    }))
  }

  // 改变布局
  // const changeLayout = (layoutType: 'vertical' | 'grid' | 'horizontal') => {
  //   // 移除布局调整功能，简化为只有模板切换和面板数量控制
  //   // 根据布局类型和面板数量智能调整画布尺寸
  //   let newCanvasWidth = currentTemplate.width
  //   let newCanvasHeight = currentTemplate.height
  //   
  //   // 根据布局类型和面板数量调整画布尺寸
  //   if (layoutType === 'vertical') {
  //     // 垂直布局：适合窄而高的画布
  //     // 宽度适中，高度根据面板数量调整
  //     newCanvasWidth = Math.max(400, Math.min(600, currentTemplate.width))
  //     const estimatedHeight = panels.length * 180 + (panels.length - 1) * 15 + 40
  //     newCanvasHeight = Math.max(estimatedHeight, 600)
  //   } else if (layoutType === 'grid') {
  //     // 网格布局：适合接近正方形的画布
  //     const cols = Math.ceil(Math.sqrt(panels.length))
  //     const rows = Math.ceil(panels.length / cols)
  //     const estimatedWidth = cols * 200 + (cols - 1) * 15 + 40
  //     const estimatedHeight = rows * 160 + (rows - 1) * 15 + 40
  //     newCanvasWidth = Math.max(estimatedWidth, 600)
  //     newCanvasHeight = Math.max(estimatedHeight, 500)
  //   } else if (layoutType === 'horizontal') {
  //     // 水平布局：适合宽而矮的画布
  //     // 高度适中，宽度根据面板数量调整
  //     const estimatedWidth = panels.length * 200 + (panels.length - 1) * 15 + 40
  //     newCanvasWidth = Math.max(estimatedWidth, 800)
  //     newCanvasHeight = Math.max(350, Math.min(500, currentTemplate.height))
  //   }
  //   
  //   // 重新计算所有面板的布局
  //   const newPanels = calculatePanelLayout(
  //     panels.length, 
  //     newCanvasWidth, 
  //     newCanvasHeight, 
  //     layoutType // 使用当前模板类型
  //   )
  //   
  //   // 保留现有面板的背景图片和自定义属性
  //   const updatedPanels = newPanels.map((newPanel, index) => {
  //     const existingPanel = panels[index]
  //     if (existingPanel) {
  //       return {
  //         ...newPanel,
  //         backgroundImage: existingPanel.backgroundImage,
  //         backgroundColor: existingPanel.backgroundColor,
  //         borderColor: existingPanel.borderColor,
  //         borderWidth: existingPanel.borderWidth,
  //         borderRadius: existingPanel.borderRadius
  //       }
  //     }
  //     return newPanel
  //   })
  //   
  //   setPanels(updatedPanels)
  //   
  //   // 更新模板为自定义模板，使用新的画布尺寸
  //   const layoutNames = {
  //     'vertical': '垂直',
  //     'grid': '网格', 
  //     'horizontal': '水平'
  //   }
  //   
  //   setCurrentTemplate(prev => ({
  //     ...prev,
  //     id: 'custom-' + Date.now(),
  //     name: `自定义模板 (${layoutNames[layoutType]}布局)`,
  //     type: 'custom',
  //     panels: updatedPanels,
  //     width: newCanvasWidth,
  //     height: newCanvasHeight,
  //     isCustom: true
  //   }))
  //   
  //   // 清空对话气泡，因为面板位置已经改变
  //   setDialogBubbles([])
  //   setSelectedBubble(null)
  // }

  const handleImageUpload = (imageData: string, file: File) => {
    // 这里可以添加图片到画布的逻辑
    console.log('Image uploaded:', file.name, imageData.slice(0, 50) + '...')
  }

  const handleExport = () => {
    setShowExportPanel(true)
  }

  // 只保留导出图片功能
  // const handleSave = () => {
  //   // 保存当前条漫数据到localStorage
  //   const comicData = {
  //     template: currentTemplate,
  //     panels,
  //     dialogBubbles,
  //     currentLayout,
  //     timestamp: new Date().toISOString()
  //   }
  //   localStorage.setItem('comic-strip-data', JSON.stringify(comicData))
  //   alert('条漫已保存到本地存储')
  // }

  // const handleImport = () => {
  //   const input = document.createElement('input')
  //   input.type = 'file'
  //   input.accept = '.json'
  //   input.onchange = (e) => {
  //     const file = (e.target as HTMLInputElement).files?.[0]
  //     if (file) {
  //       const reader = new FileReader()
  //       reader.onload = (e) => {
  //         try {
  //           const data = JSON.parse(e.target?.result as string)
  //           if (data.template && data.panels && data.dialogBubbles) {
  //             setCurrentTemplate(data.template)
  //             setPanels(data.panels)
  //             setDialogBubbles(data.dialogBubbles)
  //             if (data.currentLayout) {
  //               setCurrentLayout(data.currentLayout)
  //             }
  //             alert('条漫导入成功')
  //           }
  //         } catch (error) {
  //           alert('导入失败，文件格式不正确')
  //         }
  //       }
  //       reader.readAsText(file)
  //     }
  //   }
  //   input.click()
  // }

  const selectedBubbleData = selectedBubble 
    ? dialogBubbles.find(b => b.id === selectedBubble)
    : null

  // 侧边栏上传图片后加入素材库
  const handleAddImageAsset = (file: File, dataUrl: string) => {
    const newAsset: ImageAsset = {
      id: `asset-${Date.now()}`,
      url: dataUrl,
      name: file.name
    }
    setImageAssets(prev => [...prev, newAsset])
  }

  // 面板设置背景图片（支持侧边栏拖拽）
  const handleSetPanelBg = (panelId: string, imageUrl: string) => {
    updatePanel(panelId, { backgroundImage: imageUrl })
  }

  // 选择工作面板
  const handleSelectPanel = (panelId: string) => {
    setSelectedPanel(panelId)
  }

  // 获取当前选中面板信息
  // const selectedPanelData = panels.find(p => p.id === selectedPanel)

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Header区添加标题和水印输入框 */}
        <div className="flex items-center justify-between px-8 py-4 bg-white/80 shadow-sm sticky top-0 z-30 flex-shrink-0">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="请输入条漫标题..."
              className="text-xl font-bold px-4 py-2 rounded-lg border border-gray-300 focus:border-primary-400 outline-none bg-white/80 shadow"
              style={{ minWidth: 240 }}
            />
            <input
              type="text"
              value={watermark}
              onChange={e => setWatermark(e.target.value)}
              placeholder="水印（如@大峰AI绘画）"
              className="text-sm px-3 py-1 rounded border border-gray-200 focus:border-primary-400 outline-none bg-white/80 shadow"
              style={{ minWidth: 160 }}
            />
          </div>
          <button
            onClick={handleExport}
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold shadow transition-all"
          >
            导出图片
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <Sidebar 
            currentTemplate={currentTemplate}
            panelCount={panels.length}
            selectedPanel={selectedPanel}
            panels={panels}
            onTemplateChange={handleTemplateChange}
            onAddDialog={addDialogBubble}
            onImageUpload={handleImageUpload}
            onAddPanel={addPanel}
            onRemovePanel={removePanel}
            imageAssets={imageAssets}
            onAddImageAsset={handleAddImageAsset}
          />
          <main className="flex-1 p-6 min-h-0">
            <ComicCanvas 
              ref={canvasRef}
              template={currentTemplate}
              panels={panels}
              dialogBubbles={dialogBubbles}
              selectedBubble={selectedBubble}
              selectedPanel={selectedPanel}
              onSelectBubble={setSelectedBubble}
              onSelectPanel={handleSelectPanel}
              onUpdateBubble={updateDialogBubble}
              onDeleteBubble={deleteDialogBubble}
              onUpdatePanel={updatePanel}
              onShowPanel={() => setShowFloatingPanel(true)}
              onSetPanelBg={handleSetPanelBg}
              onAddPanel={addPanel}
              imageAssets={imageAssets}
              backgroundColor={canvasBgColor}
              title={title}
              watermark={watermark}
            />
          </main>
        </div>
        {showFloatingPanel && selectedBubbleData && (
          <FloatingPanel
            bubble={selectedBubbleData}
            onUpdate={(updates: Partial<DialogBubbleData>) => updateDialogBubble(selectedBubbleData.id, updates)}
            onClose={() => setShowFloatingPanel(false)}
          />
        )}
        {showExportPanel && (
          <ExportPanel
            canvasRef={canvasRef}
            onClose={() => setShowExportPanel(false)}
            title={title}
            watermark={watermark}
          />
        )}
        <DragOverlay>
          {activeDragId && (
            <div className="glass-card p-4 opacity-80">
              拖拽中...
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

export default App 